import React, { useEffect, useRef } from 'react';
import { Skeleton, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { getMenuItemDisplayText } from '../../utils/menuItemDisplayText';
import { InfoState, ScanLine } from './scanTypes';
import classes from './ScanMenuView.module.css';

/** A price on its own line (2,180円, ¥980〜): the AI puts it on the dish, so it isn't listed apart */
const PRICE_ONLY = /^[\s\d,.¥￥円〜~()（）+＋税込抜別価格]+$/;

/** Not a dish: the AI says so. Section names (飲み物), notes and the shop's name; a menu view shows them as headings. */
export const notADish = (state: InfoState | undefined) => state?.status === 'ready' && state.info.isMenuItem === false;

interface ScanMenuViewProps {
  lines: ScanLine[];
  infos: Record<number, InfoState>;
  /** Lines printed in English already: listed as they are, there's nothing to explain */
  english: Set<number>;
  selected: number;
  onPick: (index: number) => void;
  /** The sheet is open; the list makes room under its last row so every row can sit above it */
  sheetOpen: boolean;
  /** Viewport y the open sheet covers from */
  coverTop: () => number;
  /** Viewport px covered at the top (the view switch floating over the page) */
  topInset: number;
}

/**
 * The scanned menu as a list, in the order it's printed: each dish with its
 * English name, the Japanese and the price, as the background translation
 * fills them in. Lines that aren't dishes become headings. Tap a dish for the
 * same sheet the photo opens.
 */
export const ScanMenuView: React.FC<ScanMenuViewProps> = ({ lines, infos, english, selected, onPick, sheetOpen, coverTop, topInset }) => {
  const listRef = useRef<HTMLUListElement>(null);

  // Keep the selected dish in the space between the view switch and the sheet
  useEffect(() => {
    if (selected < 0) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-line="${selected}"]`);
    if (!row) return;
    const r = row.getBoundingClientRect(), top = topInset + 8, bottom = coverTop() - 8;
    if (r.top >= top && r.bottom <= bottom) return;
    window.scrollBy({ top: (r.top + r.bottom) / 2 - (top + bottom) / 2, behavior: 'smooth' });
  }, [selected, coverTop, topInset]);

  return (
    <ul ref={listRef} className={`${classes.list} ${sheetOpen ? classes.roomForSheet : ''}`}>
      {lines.map((l, i) => {
        if (english.has(i)) {
          return (
            <li key={i} className={classes.plain}>
              <Text fw={600}>{l.t}</Text>
              <Text size="xs" c="dimmed">Printed in English</Text>
            </li>
          );
        }
        if (l.dropped) return null;
        const state = infos[i];
        if (notADish(state)) {
          if (PRICE_ONLY.test(l.t)) return null;
          const en = state?.status === 'ready' ? getMenuItemDisplayText(state.info.item, 'name', 'en') : '';
          return (
            <li key={i} className={classes.heading}>
              <Text fw={700} size="sm" tt="uppercase" lang="ja">{l.t}</Text>
              {en && en !== l.t && <Text size="xs" c="dimmed">{en}</Text>}
            </li>
          );
        }
        const item = state?.status === 'ready' ? state.info.item : null;
        const name = l.label || (item ? getMenuItemDisplayText(item, 'name', 'en') : '');
        const description = item ? getMenuItemDisplayText(item, 'description', 'en') : '';
        const price = item ? Math.floor(Number(item.price)) : 0;
        return (
          <li key={i} data-line={i}>
            <UnstyledButton className={classes.row} onClick={() => onPick(i)} aria-current={i === selected}>
              <span className={classes.main}>
                {name ? <Text fw={600} className={classes.name}>{name}</Text> : <Skeleton height={16} width="55%" my={4} aria-label="Translating" />}
                <Text size="sm" c="dimmed" lang="ja">{l.t}</Text>
                {description && <Text size="sm" c="dimmed" lineClamp={2} mt={2}>{description}</Text>}
              </span>
              {price > 0 && <Text fw={600} size="sm" className={classes.price}>¥{price.toLocaleString('en-US')}</Text>}
              <IconChevronRight size={16} className={classes.chev} />
            </UnstyledButton>
          </li>
        );
      })}
    </ul>
  );
};
